import * as readline from "node:readline";
import { Command } from "commander";
import { logErr, logInfo, S } from "../helpers.ts";

function parseShorthand(input: string): Record<string, unknown> | null {
  const trimmed = input.trim();

  if (trimmed.startsWith("{")) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  const parts = trimmed.split(/\s+/);
  const verb = parts[0];

  switch (verb) {
    case "tools":
      return { type: "debug", query: "tools" };
    case "connectors":
      return { type: "debug", query: "connectors" };
    case "mcp":
      return { type: "debug", query: "mcp" };
    case "config":
      return { type: "debug", query: "config" };
    case "state": {
      const store = parts[1];
      return { type: "debug", query: "state", ...(store ? { args: { store } } : {}) };
    }
    case "prompt": {
      const text = parts.slice(1).join(" ");
      return { type: "prompt", prompt: text };
    }
    case "configure": {
      try {
        const json = parts.slice(1).join(" ");
        return { type: "configure", config: JSON.parse(json) };
      } catch {
        return null;
      }
    }
    case "shutdown":
      return { type: "shutdown" };
    default:
      return null;
  }
}

function formatEvent(event: Record<string, unknown>): string {
  const type = event.type as string;
  switch (type) {
    case "text":
      return String(event.content ?? "");
    case "tool_call":
      return S.dim(`[tool] ${event.tool}: ${JSON.stringify(event.input).slice(0, 80)}`);
    case "tool_result":
      return S.dim(`[result] ${event.tool}: ${JSON.stringify(event.output).slice(0, 80)}`);
    case "error":
      return S.err(`[error] ${event.message}`);
    case "debug_result":
      return `[debug:${event.query}]\n${JSON.stringify(event.data, null, 2)}`;
    case "finished": {
      const cost = typeof event.costUsd === "number" ? ` ($${event.costUsd.toFixed(4)})` : "";
      return S.dim(`[finished]${cost} ${event.summary ?? ""}`);
    }
    default:
      return S.dim(`[${type}] ${JSON.stringify(event).slice(0, 120)}`);
  }
}

/**
 * Creates the `skaile connect` command.
 *
 * Opens an interactive WebSocket REPL connected to a running agent server.
 * Supports shorthand verbs (`tools`, `connectors`, `prompt <text>`, etc.)
 * or raw JSON messages. Use `--cmd <json>` for non-interactive single-shot mode.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/misc#connect
 */
export function makeConnectCommand(): Command {
  return new Command("connect")
    .description("Interactive WebSocket REPL for a running agent server")
    .argument("<url>", "WebSocket server URL (e.g. ws://localhost:8080)")
    .option("--cmd <json>", "Send a single command and exit (non-interactive)")
    .action(async (url: string, opts: { cmd?: string }) => {
      const { WebSocketClientTransport } = await import("@skaile/workspaces/transport/ws/client");

      const client = new WebSocketClientTransport({
        url,
        reconnect: { maxAttempts: 0 },
      });

      try {
        await client.connect();
      } catch (err) {
        logErr(`Could not connect to ${url}: ${err instanceof Error ? err.message : String(err)}`);
        process.exitCode = 1;
        return;
      }

      // Non-interactive mode
      if (opts.cmd) {
        let parsed: Record<string, unknown>;
        try {
          parsed = JSON.parse(opts.cmd);
        } catch {
          logErr("--cmd value is not valid JSON");
          await client.disconnect();
          process.exitCode = 1;
          return;
        }

        await new Promise<void>((resolve) => {
          client.onMessage((event) => {
            console.log(formatEvent(event as unknown as Record<string, unknown>));
            if (event.type === "finished" || event.type === "debug_result") {
              resolve();
            }
          });
          client.send(parsed as any);
        });

        await client.disconnect();
        return;
      }

      // Interactive REPL
      console.log();
      logInfo(`Connected to ${url}`);
      logInfo("Type a command shorthand (tools, connectors, mcp, config, state, prompt <text>)");
      logInfo('or raw JSON. Type "exit" to quit.');
      console.log();

      client.onMessage((event) => {
        const line = formatEvent(event as unknown as Record<string, unknown>);
        if (line) process.stdout.write(`${line}\n`);
      });

      const rl = readline.createInterface({
        input: process.stdin,
        output: process.stdout,
        prompt: S.dim("> "),
        terminal: true,
      });

      rl.prompt();

      rl.on("line", (line) => {
        const trimmed = line.trim();
        if (!trimmed) {
          rl.prompt();
          return;
        }
        if (trimmed === "exit" || trimmed === "quit") {
          rl.close();
          return;
        }

        const cmd = parseShorthand(trimmed);
        if (!cmd) {
          logErr(`Unknown command: ${trimmed}`);
          rl.prompt();
          return;
        }

        client.send(cmd as any);
        rl.prompt();
      });

      await new Promise<void>((resolve) => {
        rl.on("close", resolve);
        client.onDisconnect(() => {
          logErr("Server disconnected");
          rl.close();
          resolve();
        });
      });

      await client.disconnect();
    });
}
